import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar as CalendarIcon,
  Users,
  Vote as VoteIcon,
  Share2,
  MoreVertical,
  Trash2,
  Archive,
  Copy,
  Star,
  Check,
  Edit,
  Repeat,
} from "lucide-react";
import { cn } from "@core/lib/utils";
import { Badge } from "@core/components/ui/badge";
import { Button } from "@core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@core/components/ui/dropdown-menu";
import { buildAbsoluteUrl } from "@core/lib/baseUrlUtils";
import { useToast } from "@core/hooks/use-toast";
import type { ConversationItem } from "./types";

export interface PollCardProps {
  item: ConversationItem;
  isSelected?: boolean;
  onToggleSelection?: () => void;
  onEdit?: (pollId: string) => void;
  onArchive?: (pollId: string) => void;
  onDelete?: (pollId: string) => void;
  onDuplicate?: (pollId: string) => void;
}

const statusVariants: Record<string, { labelKey: string; className: string }> =
  {
    active: {
      labelKey: "card.status.active",
      className: "bg-green-500/15 text-green-500 border-green-500/30",
    },
    draft: {
      labelKey: "card.status.draft",
      className: "bg-muted text-muted-foreground",
    },
    closed: {
      labelKey: "card.status.closed",
      className: "bg-blue-500/15 text-blue-500 border-blue-500/30",
    },
    finished: {
      labelKey: "card.status.closed",
      className: "bg-blue-500/15 text-blue-500 border-blue-500/30",
    },
    archived: {
      labelKey: "card.status.archived",
      className: "bg-muted text-muted-foreground",
    },
  };

/**
 * Redesigned poll card for V2 dashboard. Surfaces the top slot (y/m/n bar)
 * and an empty-state CTA when nobody has voted yet.
 *
 * Only rendered when the feature flag `enableRedesignPollsV2` is on and the
 * poll is a date poll. Behind the flag, the legacy ConversationCard is used.
 */
export function PollCard({
  item,
  isSelected = false,
  onToggleSelection,
  onEdit,
  onArchive,
  onDelete,
  onDuplicate,
}: PollCardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation("polls");
  const poll = item.poll;

  if (!poll) return null;

  const status = (poll.status ?? "active").toLowerCase();
  const statusVariant = statusVariants[status] ?? statusVariants.active;
  // Flag denormalisé posé à la création (source de vérité : recurring_series)
  const isRecurring =
    (poll as { settings?: { isRecurring?: boolean } }).settings?.isRecurring ===
    true;
  const participantCount = poll.participants_count ?? 0;
  const voteCount = poll.votes_count ?? 0;
  const topSlot = poll.topSlot ?? null;
  const slug = (poll as { slug?: string }).slug ?? poll.id;

  const voteUrl = `/poll/${slug}/vote`;
  const resultsUrl = `/poll/${slug}/results`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(buildAbsoluteUrl(voteUrl.slice(1)));
      toast({ title: t("card.copy.success") });
    } catch {
      toast({ title: t("card.copy.error"), variant: "destructive" });
    }
  };

  return (
    <article
      className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3 transition-shadow hover:shadow-md relative"
      data-testid="poll-card"
      data-poll-id={poll.id}
    >
      {/* Checkbox de sélection */}
      {onToggleSelection && (
        <div
          className="absolute top-3 right-3 z-10"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <div
            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer ${
              isSelected
                ? "bg-blue-600 border-blue-600"
                : "bg-gray-600 border-gray-400 hover:border-blue-400"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggleSelection();
            }}
            data-testid="pollcard-checkbox"
          >
            {isSelected && <Check className="w-4 h-4 text-white" />}
          </div>
        </div>
      )}

      <header
        className={cn("flex items-start gap-2", onToggleSelection && "pr-8")}
      >
        <CalendarIcon
          className="h-4 w-4 text-muted-foreground mt-1 shrink-0"
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-semibold leading-snug line-clamp-2">
            {poll.title ?? item.conversationTitle}
          </h3>
        </div>
        {isRecurring && (
          <Badge
            variant="outline"
            className="shrink-0 bg-blue-500/15 text-blue-400 border-blue-500/30 gap-1"
            title={t("recurring.badge_tooltip")}
            data-testid="poll-card-recurring-badge"
          >
            <Repeat className="h-3 w-3" aria-hidden="true" />
          </Badge>
        )}
        <Badge
          variant="outline"
          className={cn("shrink-0", statusVariant.className)}
        >
          {t(statusVariant.labelKey)}
        </Badge>
      </header>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Users className="h-3 w-3" aria-hidden="true" />
          {participantCount}{" "}
          {t("card.stats.respondents", { count: participantCount })}
        </span>
        <span className="inline-flex items-center gap-1">
          <VoteIcon className="h-3 w-3" aria-hidden="true" />
          {voteCount} {t("card.stats.votes", { count: voteCount })}
        </span>
      </div>

      {topSlot ? (
        <div
          className="rounded-lg bg-muted/40 p-2.5 text-xs"
          data-testid="poll-card-top-slot"
        >
          <div className="flex items-center gap-1.5 text-primary font-semibold text-[10px] uppercase tracking-wide mb-1">
            <Star className="h-3 w-3" aria-hidden="true" />
            {t("card.topSlot.popular")}
          </div>
          <div className="font-medium text-foreground mb-1.5 truncate">
            {topSlot.label}
            {topSlot.timeLabel ? ` · ${topSlot.timeLabel}` : ""}{" "}
            <span className="text-muted-foreground font-normal">
              ({topSlot.yes}/{topSlot.total})
            </span>
          </div>
          <VoteSplitBar
            yes={topSlot.yes}
            maybe={topSlot.maybe}
            no={topSlot.no}
            total={topSlot.total}
          />
        </div>
      ) : voteCount === 0 ? (
        <div
          className="rounded-lg border border-dashed border-border p-2.5 text-xs flex items-center justify-between gap-2"
          data-testid="poll-card-empty"
        >
          <span className="text-muted-foreground">
            {t("card.empty.noVotes")}
          </span>
          <Button size="sm" variant="secondary" onClick={handleCopyLink}>
            <Share2 className="h-3 w-3 mr-1" aria-hidden="true" />
            {t("card.actions.share")}
          </Button>
        </div>
      ) : null}

      {poll.expires_at && (
        <div
          className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
          data-testid="poll-card-expires"
        >
          <CalendarIcon className="h-3 w-3" aria-hidden="true" />
          {t("card.expiresOn", { date: formatExpiresAt(poll.expires_at) })}
        </div>
      )}

      <footer className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          variant="secondary"
          className="flex-1"
          onClick={() => navigate(resultsUrl)}
        >
          {t("card.actions.results")}
        </Button>
        <Button size="sm" className="flex-1" onClick={() => navigate(voteUrl)}>
          {t("card.actions.vote")}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              aria-label={t("card.actions.more")}
              data-testid="pollcard-menu-button"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleCopyLink}>
              <Share2 className="h-4 w-4 mr-2" /> {t("card.actions.copyLink")}
            </DropdownMenuItem>
            {onEdit && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onEdit(poll.id)}>
                  <Edit className="h-4 w-4 mr-2" /> {t("card.actions.edit")}
                </DropdownMenuItem>
              </>
            )}
            {onDuplicate && (
              <DropdownMenuItem onClick={() => onDuplicate(poll.id)}>
                <Copy className="h-4 w-4 mr-2" /> {t("card.actions.duplicate")}
              </DropdownMenuItem>
            )}
            {onArchive && (
              <DropdownMenuItem onClick={() => onArchive(poll.id)}>
                <Archive className="h-4 w-4 mr-2" /> {t("card.actions.archive")}
              </DropdownMenuItem>
            )}
            {onDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => onDelete(poll.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> {t("card.actions.delete")}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </footer>
    </article>
  );
}

/** "21 mai" / "12 février 2027" — court si année courante, long sinon */
function formatExpiresAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const months = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
  ];
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? `${d.getDate()} ${months[d.getMonth()]}`
    : `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function VoteSplitBar({
  yes,
  maybe,
  no,
  total,
}: {
  yes: number;
  maybe: number;
  no: number;
  total: number;
}) {
  const denom = Math.max(total, yes + maybe + no, 1);
  const pct = (n: number) => `${(n / denom) * 100}%`;
  return (
    <div
      className="h-[3px] w-full rounded-full bg-border/40 overflow-hidden flex"
      role="img"
      aria-label={`${yes} oui, ${maybe} peut-être, ${no} non sur ${total}`}
    >
      {yes > 0 && (
        <div className="bg-green-500 h-full" style={{ width: pct(yes) }} />
      )}
      {maybe > 0 && (
        <div className="bg-amber-400 h-full" style={{ width: pct(maybe) }} />
      )}
      {no > 0 && (
        <div className="bg-red-500 h-full" style={{ width: pct(no) }} />
      )}
    </div>
  );
}
